import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Check, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ApplyPage = () => {
  const { t, language } = useLanguage();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    firstName: '',
    email: '',
    question1: '',
    question2: '',
    question3: '',
    question4: '',
    question5: '',
    consent: false
  });

  const totalSteps = 8;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: return formData.firstName.trim().length > 0;
      case 1: return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
      case 2: return formData.question1.trim().length > 10;
      case 3: return formData.question2.trim().length > 10;
      case 4: return formData.question3.trim().length > 10;
      case 5: return formData.question4.trim().length > 0;
      case 6: return formData.question5.trim().length > 10;
      case 7: return formData.consent === true;
      default: return false;
    }
  };

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      await axios.post(`${API}/applications`, {
        email: formData.email,
        first_name: formData.firstName,
        answers: {
          question1: formData.question1,
          question2: formData.question2,
          question3: formData.question3,
          question4: formData.question4,
          question5: formData.question5
        },
        language: language
      });

      setIsSuccess(true);
    } catch (err) {
      if (err.response?.status === 400) {
        setError(t('apply.error.duplicate'));
      } else {
        setError(t('apply.error.generic'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div 
        data-testid="apply-success-page"
        className="min-h-screen flex items-center justify-center pt-24"
        style={{ backgroundColor: '#050A14' }}
      >
        <div className="max-w-xl mx-auto px-6 text-center">
          <div 
            className="w-20 h-20 mx-auto mb-10 rounded-full flex items-center justify-center"
            style={{ border: '1px solid rgba(156, 163, 175, 0.25)' }}
          >
            <Check className="w-8 h-8" style={{ color: '#9CA3AF' }} strokeWidth={1.5} />
          </div>
          <h1 
            data-testid="success-title"
            className="font-heading text-4xl md:text-5xl mb-8"
            style={{ color: '#D1D5DB' }}
          >
            {t('apply.success.title')}
          </h1>
          <p 
            className="text-base mb-6 leading-relaxed"
            style={{ color: '#9CA3AF' }}
          >
            {t('apply.success.message').replace('{name}', formData.firstName)}
          </p>
          <p 
            className="text-sm leading-relaxed mb-4"
            style={{ color: '#6B7280' }}
          >
            {t('apply.success.note')}
          </p>
          <p 
            className="text-sm italic mb-14"
            style={{ color: '#4B5563' }}
          >
            {t('apply.success.closing')}
          </p>
          <Link
            to="/"
            data-testid="success-home-btn"
            className="btn-outline inline-block"
          >
            {t('apply.success.backHome')}
          </Link>
        </div>
      </div>
    );
  }

  const stepConfigs = [
    {
      field: 'firstName',
      label: t('apply.form.firstName'),
      placeholder: t('apply.form.firstNamePlaceholder'),
      type: 'input'
    },
    {
      field: 'email',
      label: t('apply.form.email'),
      placeholder: t('apply.form.emailPlaceholder'),
      type: 'email'
    },
    {
      field: 'question1',
      label: t('apply.questions.q1.label'),
      placeholder: t('apply.questions.q1.placeholder'),
      type: 'textarea'
    },
    {
      field: 'question2',
      label: t('apply.questions.q2.label'),
      placeholder: t('apply.questions.q2.placeholder'),
      type: 'textarea'
    },
    {
      field: 'question3',
      label: t('apply.questions.q3.label'),
      placeholder: t('apply.questions.q3.placeholder'),
      type: 'textarea'
    },
    {
      field: 'question4',
      label: t('apply.questions.q4.label'),
      placeholder: t('apply.questions.q4.placeholder'),
      type: 'input'
    },
    {
      field: 'question5',
      label: t('apply.questions.q5.label'),
      placeholder: t('apply.questions.q5.placeholder'),
      type: 'textarea'
    },
    {
      field: 'consent',
      label: language === 'fr' 
        ? "J'accepte la politique de confidentialité" 
        : "I accept the privacy policy",
      type: 'checkbox'
    }
  ];

  const config = stepConfigs[currentStep];

  return (
    <div 
      data-testid="apply-page"
      className="min-h-screen flex items-center justify-center pt-24 pb-12"
      style={{ backgroundColor: '#050A14' }}
    >
      <div className="w-full max-w-xl mx-auto px-6">
        {/* Header */}
        <div className="mb-16 text-center">
          <p 
            className="font-body text-xs tracking-[0.25em] uppercase mb-3"
            style={{ color: '#6B7280' }}
          >
            {t('apply.progress')} {currentStep + 1} {t('apply.of')} {totalSteps}
          </p>
          {/* Progress Bar */}
          <div 
            className="w-full max-w-xs mx-auto h-px overflow-hidden"
            style={{ backgroundColor: 'rgba(156, 163, 175, 0.1)' }}
          >
            <div 
              className="h-full transition-all duration-500"
              style={{ 
                width: `${((currentStep + 1) / totalSteps) * 100}%`,
                backgroundColor: '#9CA3AF'
              }}
            />
          </div>
        </div>

        {/* Step Content */}
        <div className="animate-fade-in" key={currentStep}>
          <label 
            data-testid={`step-${currentStep}-label`}
            className="block font-heading text-2xl md:text-3xl mb-10 text-center"
            style={{ color: '#D1D5DB' }}
          >
            {config.type !== 'checkbox' && config.label}
          </label>

          {config.type === 'textarea' ? (
            <textarea
              data-testid={`input-${config.field}`}
              value={formData[config.field]}
              onChange={(e) => handleInputChange(config.field, e.target.value)}
              placeholder={config.placeholder}
              className="w-full bg-transparent text-base py-4 px-0 resize-none h-32 font-body focus:outline-none transition-colors text-center"
              style={{ 
                color: '#D1D5DB',
                borderBottom: '1px solid rgba(156, 163, 175, 0.15)'
              }}
              autoFocus
            />
          ) : config.type === 'checkbox' ? (
            <div className="flex flex-col items-center gap-6">
              <label className="flex items-center gap-4 cursor-pointer group">
                <input
                  type="checkbox"
                  data-testid={`input-${config.field}`}
                  checked={formData[config.field]}
                  onChange={(e) => handleInputChange(config.field, e.target.checked)}
                  className="w-5 h-5 rounded-sm border-2 appearance-none cursor-pointer transition-all"
                  style={{ 
                    borderColor: formData[config.field] ? '#9CA3AF' : 'rgba(156, 163, 175, 0.3)',
                    backgroundColor: formData[config.field] ? '#9CA3AF' : 'transparent'
                  }}
                />
                <span 
                  className="font-body text-base"
                  style={{ color: '#D1D5DB' }}
                >
                  {config.label}
                </span>
              </label>
              <Link
                to="/confidentialite"
                target="_blank"
                className="text-sm underline transition-colors"
                style={{ color: '#6B7280' }}
              >
                {language === 'fr' ? 'Lire la politique de confidentialité' : 'Read the privacy policy'}
              </Link>
            </div>
          ) : (
            <input
              type={config.type === 'email' ? 'email' : 'text'}
              data-testid={`input-${config.field}`}
              value={formData[config.field]}
              onChange={(e) => handleInputChange(config.field, e.target.value)}
              placeholder={config.placeholder}
              className="w-full bg-transparent text-base py-4 px-0 font-body focus:outline-none transition-colors text-center"
              style={{ 
                color: '#D1D5DB',
                borderBottom: '1px solid rgba(156, 163, 175, 0.15)'
              }}
              autoFocus
            />
          )}

          {error && (
            <p data-testid="error-message" className="mt-6 text-sm text-center" style={{ color: '#EF4444' }}>
              {error}
            </p>
          )}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-16">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 0}
            data-testid="apply-prev-btn"
            className={`flex items-center gap-2 transition-colors ${currentStep === 0 ? 'opacity-0 pointer-events-none' : ''}`}
            style={{ color: '#6B7280' }}
          >
            <ArrowLeft size={18} strokeWidth={1.5} />
            <span className="font-body text-xs tracking-[0.15em] uppercase">
              {t('apply.previous')}
            </span>
          </button>

          {currentStep === totalSteps - 1 ? (
            <button
              onClick={handleSubmit}
              disabled={!canProceed() || isSubmitting}
              data-testid="apply-submit-btn"
              className={`btn-silver flex items-center gap-3 ${
                !canProceed() || isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <span>{t('apply.submit')}</span>
                  <Check size={16} strokeWidth={1.5} />
                </>
              )}
            </button>
          ) : (
            <button
              onClick={handleNext}
              disabled={!canProceed()}
              data-testid="apply-next-btn"
              className={`flex items-center gap-2 transition-colors ${!canProceed() ? 'opacity-50 cursor-not-allowed' : ''}`}
              style={{ color: '#9CA3AF' }}
            >
              <span className="font-body text-xs tracking-[0.15em] uppercase">
                {t('apply.next')}
              </span>
              <ArrowRight size={18} strokeWidth={1.5} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApplyPage;
